// Resolver type-ikony agendy. Pořadí: 1) přesný klíč (GlobalName), 2) ObjectCategory
// prefix (část před první tečkou), 3) substring heuristika (portovaná z prototypu
// `icons.jsx → typeIconKey`), 4) jinak `null` → decentní tečka.
//
// Doporučení: explicitní přiřazení (EXACT_KEY / CATEGORY) je zdroj pravdy; heuristika
// je jen fallback pro agendy bez záznamu.

import type { MenuIconName, MenuIconResolver } from './menuTypes';

/** Přesné přiřazení dle GlobalName agendy. */
const EXACT_KEY: Record<string, MenuIconName> = {
  'Person.List': 'users',
  'Organization.List': 'building',
  'AccommodationFacility.List': 'bed',
  'AirTransport.List': 'flight',
  'RoadTransport.List': 'bus',
  'RailTransport.List': 'train',
  'WaterTransport.List': 'ship',
  'Vehicle.List': 'car',
  'Destination.List': 'pin',
  'Directory.List': 'doc',
  'Event.List': 'calendar',
  'Product.List': 'tag',
  'Goods.List': 'tag',
  'BusinessCase.List': 'list',
  'Payment.List': 'coin',
  'AccountingDoc.List': 'coin',
};

/** Přiřazení dle ObjectCategory (prefix před první tečkou). */
const CATEGORY: Record<string, MenuIconName> = {
  Person: 'users',
  Organization: 'building',
  AccommodationFacility: 'bed',
  CulturalFacility: 'bed',
  AirTransport: 'flight',
  RoadTransport: 'bus',
  RailTransport: 'train',
  WaterTransport: 'ship',
  Vehicle: 'car',
  Destination: 'pin',
  Market: 'pin',
  Directory: 'doc',
  Event: 'calendar',
  Product: 'tag',
  Goods: 'tag',
  Payment: 'coin',
  AccountingDoc: 'coin',
  CashAccount: 'coin',
};

/** Substring heuristika nad titulkem + klíčem (poslední záchrana). */
function heuristic(text: string): MenuIconName | null {
  const s = text.toLowerCase();
  const has = (...arr: string[]) => arr.some((x) => s.includes(x));
  if (
    has(
      'osob',
      'person',
      'cestuj',
      'uživatel',
      'uzivatel',
      'reklamuj',
      'lidsk',
      'workperson',
      'delegát',
      'delegat',
      'příjemc',
      'prijemc',
      'role'
    )
  )
    return 'users';
  if (has('organiz', 'subinstance', 'prodejc', 'seller', 'firm')) return 'building';
  if (has('ubytov', 'hotel', 'pokoj', 'rooming', 'room', 'accommodation', 'nominac')) return 'bed';
  if (has('letec', 'letenk', 'lety', 'amadeus', 'ssim', 'ssm', 'avíz', 'aviz', 'flight', 'air'))
    return 'flight';
  if (has('autokar', 'road', 'jízd', 'jizd', 'bus')) return 'bus';
  if (has('železni', 'zelezni', 'rail', 'vlak', 'train')) return 'train';
  if (has('lodní', 'lodni', 'water', 'loď')) return 'ship';
  if (has('voz', 'vehicle', 'fleet')) return 'car';
  if (
    has(
      'platb',
      'plateb',
      'faktur',
      'poklad',
      'účet',
      'ucet',
      'účt',
      'provi',
      'marž',
      'marz',
      'slev',
      'cen',
      'dph',
      'záloh',
      'zaloh',
      'kurz',
      'invoice',
      'payment',
      'accounting',
      'mzd',
      'salary',
      'terminál',
      'bilanc',
      'kontac',
      'příráž',
      'priraz'
    )
  )
    return 'coin';
  if (
    has(
      'soubor',
      'tisk',
      'dokument',
      'šablon',
      'sablon',
      'adresá',
      'adresa',
      'obrázk',
      'obrazk',
      'voucher',
      'certifik'
    )
  )
    return 'doc';
  if (
    has(
      'konfigur',
      'nastav',
      'config',
      'api',
      'restart',
      'démon',
      'daemon',
      'log',
      'import',
      'export',
      'synchroniz',
      'validač',
      'validac',
      'machine',
      'automat',
      'systém',
      'system',
      'proces'
    )
  )
    return 'cog';
  if (
    has(
      'web',
      'stránk',
      'strank',
      'sekce',
      'newslet',
      'banner',
      'url',
      'menu',
      'dotazník',
      'dotaznik',
      'survey',
      'článk',
      'clank',
      'slovník',
      'slovnik',
      'page',
      'redirect',
      'přesměr',
      'presmer',
      'proměn',
      'promen'
    )
  )
    return 'globe';
  if (
    has(
      'událost',
      'udalost',
      'termín',
      'termin',
      'sezón',
      'sezon',
      'akce',
      'aukce',
      'doby',
      'novink',
      'event',
      'turistick',
      'období',
      'obdobi',
      'storno'
    )
  )
    return 'calendar';
  if (has('vyhledá', 'vyhleda', 'search', 'hledá', 'columbus', 'jádro', 'jadro')) return 'search';
  if (has('destinac', 'trh', 'market', 'míst', 'mist', 'meeting')) return 'pin';
  if (has('produkt', 'zboží', 'zbozi', 'goods', 'katalog', 'omezení prodej')) return 'tag';
  if (
    has(
      'seznam',
      'přehled',
      'prehled',
      'statistik',
      'tabulk',
      'číseln',
      'ciseln',
      'report',
      'plachta',
      'sestav',
      'reklamac',
      'projekt',
      'úkol',
      'ukol',
      'skupin',
      'poznámk',
      'poznamk',
      'komunik',
      'crm'
    )
  )
    return 'list';
  return null;
}

/** Odvodí ObjectCategory z GlobalName (část před první tečkou). */
function categoryOf(key: string): string {
  const dot = key.indexOf('.');
  return dot === -1 ? key : key.slice(0, dot);
}

/** Výchozí resolver type-ikony. */
export const menuTypeIcon: MenuIconResolver = (node) => {
  const key = node.key ?? '';
  if (key && EXACT_KEY[key]) return EXACT_KEY[key];
  if (key) {
    const cat = categoryOf(key);
    if (CATEGORY[cat]) return CATEGORY[cat];
  }
  return heuristic(`${node.title} ${key}`);
};
